import { isNil } from 'lodash-es';
import { z } from 'zod';
import { checkSchema } from '../../utils/zod';
import { hasAccountPermission } from '../User/User';
import type { UserRole } from '../User/UserRole';
import {
  type ProgrammingPlanContext,
  ProgrammingPlanContextList
} from './Context';
import { DistributionKind } from './DistributionKind';
import { ProgrammingPlanDomainId } from './ProgrammingPlanDomain';
import {
  ProgrammingPlanDepartmentalStatus,
  ProgrammingPlanNationalStatus,
  ProgrammingPlanRegionalStatus
} from './ProgrammingPlanLocalStatus';
import { ProgrammingPlanNationalCoordinator } from './ProgrammingPlanNationalCoordinator';
import { ProgrammingPlanSettings } from './ProgrammingPlanSettings';
import { ProgrammingSubPlan } from './ProgrammingSubPlan';

export const ProgrammingPlanBase = z.object({
  id: z.guid(),
  domainId: ProgrammingPlanDomainId,
  title: z.string().min(1, 'Veuillez renseigner le titre.'),
  subPlans: z.array(ProgrammingSubPlan),
  distributionKind: DistributionKind,
  nationalCoordinators: z.array(ProgrammingPlanNationalCoordinator),
  ...ProgrammingPlanSettings.shape,
  settingsCompleted: z.boolean(),
  createdAt: z.coerce.date(),
  createdBy: z.guid(),
  year: z.number(),
  nationalStatus: ProgrammingPlanNationalStatus,
  regionalStatus: z.array(ProgrammingPlanRegionalStatus),
  departmentalStatus: z.array(ProgrammingPlanDepartmentalStatus),
  closedAt: z.coerce.date().nullish(),
  closedBy: z.guid().nullish(),
  launchedAt: z.coerce.date().nullish(),
  launchedBy: z.guid().nullish()
});

export const ProgrammingPlanChecked = checkSchema(
  ProgrammingPlanBase,
  (ctx) => {
    if (ctx.value.launchedAt && !ctx.value.launchedBy) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message: 'Veuillez renseigner launchedBy si launchedAt est renseigné',
        path: ['launchedBy']
      });
    }
    if (ctx.value.closedAt && !ctx.value.closedBy) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message: 'Veuillez renseigner closedBy si closedAt est renseigné',
        path: ['closedBy']
      });
    }
    if (
      ctx.value.closedAt &&
      ctx.value.regionalStatus.some((status) => status.status !== 'Closed')
    ) {
      ctx.issues.push({
        input: ctx.value,
        code: 'custom',
        message: 'Status régional doit être "Closed" si closedAt est renseigné',
        path: ['regionalStatus']
      });
    }
  }
);

export type ProgrammingPlanChecked = z.infer<typeof ProgrammingPlanChecked>;

export const ProgrammingPlanCreateInput = z.object({
  domainId: ProgrammingPlanDomainId,
  title: z
    .string({ error: () => 'Veuillez renseigner le nom du plan.' })
    .trim()
    .min(1, { error: () => 'Veuillez renseigner le nom du plan.' })
});

export type ProgrammingPlanCreateInput = z.infer<
  typeof ProgrammingPlanCreateInput
>;

export const programmingPlanContexts = (plan: {
  subPlans: Pick<ProgrammingSubPlan, 'context'>[];
}): ProgrammingPlanContext[] =>
  ProgrammingPlanContextList.filter((context) =>
    plan.subPlans.some((subPlan) => subPlan.context === context)
  );

export const isCampaignLaunched = (
  plan: Pick<ProgrammingPlanChecked, 'launchedAt'>
): boolean => !isNil(plan.launchedAt);

export const isClosed = (plan: ProgrammingPlanChecked): boolean => {
  return !isNil(plan.closedAt);
};

export const isProgrammingPlanDeletable = (
  plan: Pick<ProgrammingPlanChecked, 'nationalStatus'>,
  userRoles: UserRole[]
): boolean =>
  hasAccountPermission(userRoles, 'administrationMaestro') ||
  ['InProgress', 'SubmittedToAdmin'].includes(plan.nationalStatus.status);

export const hasNewerLaunchedCampaign = (
  plan: Pick<ProgrammingPlanChecked, 'domainId' | 'title' | 'year'>,
  plans: Pick<
    ProgrammingPlanChecked,
    'domainId' | 'title' | 'year' | 'launchedAt'
  >[],
  domainLabelById: Map<string, string>
): boolean =>
  plans.some(
    (other) =>
      other.title === plan.title &&
      domainLabelById.get(other.domainId) ===
        domainLabelById.get(plan.domainId) &&
      other.year > plan.year &&
      !isNil(other.launchedAt)
  );

export const ProgrammingPlanSort = (
  a: ProgrammingPlanChecked,
  b: ProgrammingPlanChecked
) => b.year - a.year || a.title.localeCompare(b.title);
